import React from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Box,
  IconButton,
  Tooltip
} from "@mui/material"
import { styled } from "@mui/system"
import { InfoOutlined } from "@mui/icons-material"
import UserDetailsModal from "./userDetailsModal"
import styles from "../../AnalyticsDashBoard/analytics.module.scss"
import { objectMetrics, summaryGroups } from "helpers/constant"

const StyledTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  "& .MuiTableCell-root": {
    padding: "14px 24px 12px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: `2px solid ${theme.palette.background.default}`,
    boxSizing: "border-box",
    color: theme.palette.text.primary,
    "&:first-of-type": {
      paddingLeft: 6,
      paddingRight: 27
    },
    "&:last-child": {
      paddingRight: 0
    }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "relative",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none !important",
    "&:last-child": {
      paddingRight: 0
    },
    "&:first-of-type": {
      borderBottom: "none !important"
    }
  }
}))

const StyledHeaderGroupCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "12px",
  fontWeight: 400,
  textTransform: "uppercase",
  paddingBottom: "8px !important",
  borderRadius: "8px !important",
  textAlign: "center",
  backgroundColor: `${theme.palette.drawer.main} !important`,
  border: `4px solid ${theme.palette.background.main}`,
  backgroundClip: "padding-box",
  "&:last-child": {
    borderRight: "none"
  }
}))

const AnalyticsTable = ({
  selectedMetrics,
  allMetrics,
  data,
  loading,
  currentPage,
  itemsPerPage,
  isCompare
}) => {
  const [modalState, setModalState] = React.useState({
    open: false,
    metricLabel: "",
    data: null
  })

  const handleOpenModal = (label, metricData) => {
    setModalState({
      open: true,
      metricLabel: label,
      data: metricData
    })
  }

  const handleCloseModal = () => {
    setModalState((prev) => ({ ...prev, open: false }))
  }

  const activeMetrics = allMetrics
    .filter((m) => selectedMetrics?.includes(m.value))
    .filter((m) => !summaryGroups?.some((g) => g.key === m.value))

  const activeSummaryGroups = summaryGroups?.filter((group) => selectedMetrics?.includes(group.key))

  const awolIncluded = activeMetrics?.some((m) => m.value === "awol_users")
  const analyticsColSpan =
    activeMetrics?.length * (isCompare ? 3 : 1) - (isCompare && awolIncluded ? 2 : 0)

  const summaryColSpan = activeSummaryGroups?.length * (isCompare ? 5 : 2)
  const totalColSpan = 2 + analyticsColSpan + summaryColSpan

  const renderMetricValue = (row, metricKey) => {
    if (!row) return "-"
    const value = row[metricKey]
    if (value === undefined || value === null) return "-"

    if (objectMetrics[metricKey]) {
      const tooltipContent = objectMetrics[metricKey]
        .map((k) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${value[k] ?? "-"}`)
        .join(" | ")

      return {
        display: value.total ?? "-",
        tooltip: tooltipContent
      }
    }

    if (typeof value === "object") {
      return value.count ?? value.total ?? "-"
    }

    return value
  }

  const renderMetricCell = (rowSource, metric, isExpandable) => {
    const metricResult = renderMetricValue(rowSource, metric?.value)
    const isObjectValue = typeof metricResult === "object" && metricResult.tooltip
    const displayValue = isObjectValue ? metricResult.display : metricResult
    const hasUsers = rowSource[metric?.value]?.users?.length > 0
    const showIcon = isObjectValue || (isExpandable && hasUsers)

    return (
      <TableCell align="center" key={metric.value}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%"
          }}>
          <Box
            sx={{
              minWidth: showIcon ? "40px" : "0px",
              textAlign: showIcon ? "right" : "center",
              pr: showIcon ? 0.5 : 0
            }}>
            <Typography variant="body1Regular">{displayValue}</Typography>
          </Box>
          {showIcon && (
            <Box sx={{ width: "24px", display: "flex", justifyContent: "flex-start" }}>
              {isObjectValue && (
                <Tooltip title={metricResult.tooltip} arrow>
                  <IconButton size="small" sx={{ p: 0.5, color: "primary.main" }}>
                    <InfoOutlined sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              {isExpandable && hasUsers && (
                <IconButton
                  size="small"
                  onClick={() => handleOpenModal(metric?.label, rowSource[metric?.value])}
                  sx={{ p: 0.5, color: "primary.main" }}>
                  <InfoOutlined sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </TableCell>
    )
  }

  const renderPercentageChangeCell = (row, metric) => {
    let pctValue = row?.percentage_change?.[metric.value]
    if (pctValue === undefined || pctValue === null) {
      return (
        <TableCell align="center" key={`${metric.value}-pct`}>
          <Typography variant="body1Regular">-</Typography>
        </TableCell>
      )
    }

    let displayValue = ""
    let tooltipContent = null

    if (objectMetrics[metric.value]) {
      tooltipContent = objectMetrics[metric.value]
        .map((k) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${pctValue[k] ?? "-"}%`)
        .join(" | ")
      displayValue = `${pctValue.total ?? "-"}%`
    } else if (typeof pctValue === "object") {
      displayValue = `${pctValue.count ?? pctValue.total ?? "-"}%`
    } else {
      displayValue = `${pctValue}%`
    }

    let color = "text.primary"
    if (displayValue !== "-%" && displayValue !== "%" && displayValue !== "-") {
      const numericValue = parseFloat(displayValue)
      if (numericValue > 0) color = "success.main"
      else if (numericValue < 0) color = "error.main"
    } else {
      displayValue = "-"
    }

    return (
      <TableCell
        align="center"
        key={`${metric.value}-pct`}
        sx={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}>
        <Box
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
          <Typography variant="body1Regular" sx={{ color }}>
            {displayValue}
          </Typography>
          {tooltipContent && (
            <Tooltip title={tooltipContent} arrow>
              <IconButton size="small" sx={{ p: 0.5, color: "primary.main" }}>
                <InfoOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    )
  }

  const renderSimplePercentageChange = (row, groupKey, idx) => {
    let pctValue = row?.percentage_change?.[groupKey]?.count
    if (pctValue === undefined || pctValue === null) {
      return (
        <TableCell
          align="center"
          key={`${groupKey}-pct`}
          className={idx % 2 === 0 && styles.evenCell}
          sx={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}>
          <Typography variant="body1Regular">-</Typography>
        </TableCell>
      )
    }

    let color = "text.primary"
    const numericValue = parseFloat(pctValue)
    if (numericValue > 0) color = "success.main"
    else if (numericValue < 0) color = "error.main"

    return (
      <TableCell
        align="center"
        key={`${groupKey}-pct`}
        className={idx % 2 === 0 && styles.evenCell}
        sx={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}>
        <Typography variant="body1Regular" sx={{ color }}>
          {pctValue}%
        </Typography>
      </TableCell>
    )
  }

  if (!loading && (!selectedMetrics || selectedMetrics?.length === 0)) {
    return (
      <Box
        sx={{
          width: "100%",
          py: 10,
          textAlign: "center",
          backgroundColor: "background.main",
          borderRadius: "8px"
        }}>
        <Typography variant="body1Regular" sx={{ fontStyle: "italic", color: "text.secondary" }}>
          Please select the metrics and generate the data
        </Typography>
      </Box>
    )
  }

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 800 }}>
      {loading && (
        <Box sx={{ width: "100%", textAlign: "center", p: 2 }}>
          <Typography variant="body1">Please wait we are fetching the data...</Typography>
        </Box>
      )}
      <StyledTable stickyHeader aria-label="grouped analytics table">
        <TableHead className={styles.tableHead}>
          <TableRow>
            <StyledHeaderGroupCell colSpan={2} className={styles.countCell}>
              Restaurant
            </StyledHeaderGroupCell>
            {activeMetrics.length > 0 && (
              <StyledHeaderGroupCell colSpan={analyticsColSpan}>
                Analytics Dashboard Data
              </StyledHeaderGroupCell>
            )}
            {activeSummaryGroups.map((group) => (
              <StyledHeaderGroupCell key={group.key} colSpan={isCompare ? 5 : 2}>
                {group.label}
              </StyledHeaderGroupCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell className={styles.countCell} rowSpan={isCompare ? 2 : 1}>
              <Typography variant="body2Regular" className={styles.headerText}>
                #
              </Typography>
            </TableCell>
            <TableCell className={styles.nameCell} rowSpan={isCompare ? 2 : 1}>
              <Typography variant="body2Regular" className={styles.headerText}>
                Restaurant Name
              </Typography>
            </TableCell>
            {activeMetrics?.map((metric) => (
              <TableCell
                key={metric?.value}
                align="center"
                className={styles.quizCell}
                sx={{ background: isCompare ? "theme.palette.drawer.main !important" : "" }}
                colSpan={isCompare && metric.value !== "awol_users" ? 3 : 1}>
                <Typography variant="body2Regular" className={styles.headerText}>
                  {metric?.label}
                </Typography>
              </TableCell>
            ))}
            {activeSummaryGroups?.map((group, idx) => (
              <React.Fragment key={`${group.key}-sub`}>
                <TableCell
                  align="center"
                  colSpan={isCompare ? 3 : 1}
                  sx={{ minWidth: "350px" }}
                  className={idx % 2 === 0 && styles.evenCell}>
                  <Typography variant="body2Regular" className={styles.headerText}>
                    Count
                  </Typography>
                </TableCell>
                <TableCell
                  align="center"
                  colSpan={isCompare ? 2 : 1}
                  sx={{ minWidth: "250px" }}
                  className={idx % 2 === 0 && styles.evenCell}>
                  <Typography variant="body2Regular" className={styles.headerText}>
                    Percentage
                  </Typography>
                </TableCell>
              </React.Fragment>
            ))}
          </TableRow>
          {isCompare && (
            <TableRow>
              {activeMetrics?.map((metric) => {
                if (metric.value === "awol_users") {
                  return (
                    <TableCell
                      key={`${metric?.value}-ranges`}
                      align="center"
                      className={styles.quizCell}>
                      <Typography variant="body2Regular" className={styles.headerText}>
                        -
                      </Typography>
                    </TableCell>
                  )
                }
                return (
                  <React.Fragment key={`${metric?.value}-ranges`}>
                    <TableCell align="center" className={styles.quizCell}>
                      <Typography variant="body2Regular" className={styles.headerText}>
                        Range 1
                      </Typography>
                    </TableCell>
                    <TableCell align="center" className={styles.quizCell}>
                      <Typography variant="body2Regular" className={styles.headerText}>
                        Range 2
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      className={styles.quizCell}
                      sx={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}>
                      <Typography variant="body2Regular" className={styles.headerText}>
                        % Change
                      </Typography>
                    </TableCell>
                  </React.Fragment>
                )
              })}
              {activeSummaryGroups?.map((group, idx) => (
                <React.Fragment key={`${group.key}-ranges`}>
                  <TableCell align="center" className={idx % 2 === 0 && styles.evenCell}>
                    <Typography variant="body2Regular" className={styles.headerText}>
                      Range 1
                    </Typography>
                  </TableCell>
                  <TableCell align="center" className={idx % 2 === 0 && styles.evenCell}>
                    <Typography variant="body2Regular" className={styles.headerText}>
                      Range 2
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="center"
                    className={idx % 2 === 0 && styles.evenCell}
                    sx={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}>
                    <Typography variant="body2Regular" className={styles.headerText}>
                      % Change
                    </Typography>
                  </TableCell>
                  <TableCell align="center" className={idx % 2 === 0 && styles.evenCell}>
                    <Typography variant="body2Regular" className={styles.headerText}>
                      Range 1
                    </Typography>
                  </TableCell>
                  <TableCell align="center" className={idx % 2 === 0 && styles.evenCell}>
                    <Typography variant="body2Regular" className={styles.headerText}>
                      Range 2
                    </Typography>
                  </TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {(data || [])?.map((row, index) => {
            const rowSource = row.range_1 || row
            return (
              <TableRow key={row.id}>
                <TableCell className={styles.countCell}>
                  <Typography variant="body2">
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </Typography>
                </TableCell>
                <TableCell align="left" className={styles.nameCell} sx={{ minWidth: 250 }}>
                  <Typography variant="body1Regular">{row?.name}</Typography>
                </TableCell>
                {activeMetrics?.map((metric) => {
                  const isExpandable = metric.value === "awol_users"
                  if (isCompare && metric.value !== "awol_users") {
                    const range1Data = row?.range_1 || row
                    return (
                      <React.Fragment key={metric.value}>
                        {React.cloneElement(renderMetricCell(range1Data, metric, isExpandable), {
                          key: `${metric.value}-1`
                        })}
                        {React.cloneElement(
                          renderMetricCell(row?.range_2 || {}, metric, isExpandable),
                          { key: `${metric.value}-2` }
                        )}
                        {renderPercentageChangeCell(row, metric)}
                      </React.Fragment>
                    )
                  }
                  return renderMetricCell(rowSource, metric, isExpandable)
                })}
                {activeSummaryGroups?.map((group, idx) => {
                  if (isCompare) {
                    return (
                      <React.Fragment key={`${group.key}-data`}>
                        <TableCell align="center" className={idx % 2 === 0 && styles.evenCell}>
                          <Box className="d-flex align-items-center justify-content-center">
                            {(() => {
                              const hasUsers =
                                row?.range_1?.[group.key]?.users?.length > 0 ||
                                row[group.key]?.users?.length > 0
                              return (
                                <>
                                  <Box
                                    sx={{
                                      minWidth: hasUsers ? "40px" : "0px",
                                      textAlign: hasUsers ? "right" : "center",
                                      pr: hasUsers ? 0.5 : 0
                                    }}>
                                    <Typography variant="body1Regular">
                                      {row?.range_1?.[group.key]?.count ??
                                        row[group.key]?.count ??
                                        "-"}
                                    </Typography>
                                  </Box>
                                  {hasUsers && (
                                    <Box
                                      sx={{
                                        width: "24px",
                                        display: "flex",
                                        justifyContent: "flex-start"
                                      }}>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleOpenModal(
                                            group.label,
                                            row?.range_1?.[group.key] || row[group.key]
                                          )
                                        }
                                        sx={{ p: 0.5, color: "primary.main" }}>
                                        <InfoOutlined sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Box>
                                  )}
                                </>
                              )
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell align="center" className={idx % 2 === 0 && styles.evenCell}>
                          <Box className="d-flex align-items-center justify-content-center">
                            {(() => {
                              const hasUsers = row?.range_2?.[group.key]?.users?.length > 0
                              return (
                                <>
                                  <Box
                                    sx={{
                                      minWidth: hasUsers ? "40px" : "0px",
                                      textAlign: hasUsers ? "right" : "center",
                                      pr: hasUsers ? 0.5 : 0
                                    }}>
                                    <Typography variant="body1Regular">
                                      {row?.range_2?.[group.key]?.count ?? "-"}
                                    </Typography>
                                  </Box>
                                  {hasUsers && (
                                    <Box
                                      sx={{
                                        width: "24px",
                                        display: "flex",
                                        justifyContent: "flex-start"
                                      }}>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleOpenModal(group.label, row?.range_2[group.key])
                                        }
                                        sx={{ p: 0.5, color: "primary.main" }}>
                                        <InfoOutlined sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Box>
                                  )}
                                </>
                              )
                            })()}
                          </Box>
                        </TableCell>

                        {renderSimplePercentageChange(row, group.key, idx)}

                        <TableCell align="center" className={idx % 2 === 0 && styles.evenCell}>
                          <Typography variant="body1Regular">
                            {row?.range_1?.[group.key]?.percentage ??
                              row[group.key]?.percentage ??
                              "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" className={idx % 2 === 0 && styles.evenCell}>
                          <Typography variant="body1Regular">
                            {row?.range_2?.[group.key]?.percentage ?? "-"}
                          </Typography>
                        </TableCell>
                      </React.Fragment>
                    )
                  }

                  return (
                    <React.Fragment key={`${group.key}-data`}>
                      <TableCell align="center" className={idx % 2 === 0 && styles.evenCell}>
                        <Box className="d-flex align-items-center justify-content-center">
                          {(() => {
                            const hasUsers = rowSource[group.key]?.users?.length > 0
                            return (
                              <>
                                <Box
                                  sx={{
                                    minWidth: hasUsers ? "40px" : "0px",
                                    textAlign: hasUsers ? "right" : "center",
                                    pr: hasUsers ? 0.5 : 0
                                  }}>
                                  <Typography variant="body1Regular">
                                    {rowSource[group.key]?.count ?? "-"}
                                  </Typography>
                                </Box>
                                {hasUsers && (
                                  <Box
                                    sx={{
                                      width: "24px",
                                      display: "flex",
                                      justifyContent: "flex-start"
                                    }}>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleOpenModal(group.label, rowSource[group.key])
                                      }
                                      sx={{ p: 0.5, color: "primary.main" }}>
                                      <InfoOutlined sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Box>
                                )}
                              </>
                            )
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell align="center" className={idx % 2 === 0 && styles.evenCell}>
                        <Typography variant="body1Regular">
                          {rowSource[group.key]?.percentage ?? "-"}
                        </Typography>
                      </TableCell>
                    </React.Fragment>
                  )
                })}
              </TableRow>
            )
          })}
          {!loading && selectedMetrics?.length > 0 && (!data || data.length === 0) && (
            <TableRow>
              <TableCell colSpan={totalColSpan}>
                <Typography variant="body1" align="center">
                  No data found for selected period
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            (!selectedMetrics || selectedMetrics?.length === 0) &&
            (!data || data.length === 0) && (
              <TableRow>
                <TableCell colSpan={totalColSpan}>
                  <Typography
                    variant="body1"
                    align="center"
                    sx={{ py: 4, fontStyle: "italic", color: "text.secondary" }}>
                    Please select the metrics and generate the data
                  </Typography>
                </TableCell>
              </TableRow>
            )}
        </TableBody>
      </StyledTable>
      <UserDetailsModal
        open={modalState.open}
        onClose={handleCloseModal}
        metricLabel={modalState.metricLabel}
        data={modalState.data}
      />
    </TableContainer>
  )
}

export default AnalyticsTable
